//#region src/duration/duration.constant.ts
const HOURS_IN_DAY = 24;
const DAYS_IN_WEEK = 7;
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 1440;
const ONE_SECOND_IN_MS = 1e3;
const ONE_MINUTE_IN_MS = ONE_SECOND_IN_MS * 60;
const QUARTER_IN_MS = 15 * ONE_MINUTE_IN_MS;
const ONE_HOUR_IN_MS = ONE_MINUTE_IN_MS * 60;
const TWO_HOURS_IN_MS = 2 * ONE_HOUR_IN_MS;
const ONE_DAY_IN_MS = ONE_HOUR_IN_MS * 24;
const ONE_YEAR_IN_MS = ONE_DAY_IN_MS * 365;
const THIRTY_SECONDS_IN_MS = 30 * ONE_SECOND_IN_MS;
const ONE_WEEK_IN_MS = ONE_DAY_IN_MS * 7;
const ONE_DAY_IN_SECONDS = ONE_DAY_IN_MS / ONE_SECOND_IN_MS;
const ONE_WEEK_IN_SECONDS = 7 * ONE_DAY_IN_SECONDS;
//#endregion
//#region src/period/period.error.ts
const END_BEFORE_START_ERROR_MESSAGE = "La date de fin doit être après la date de début";
const NO_DURATION_ERROR_MESSAGE = "La période doit avoir une durée";
var PeriodError = class extends Error {};
var EndBeforeStart = class extends PeriodError {
	constructor() {
		super(END_BEFORE_START_ERROR_MESSAGE);
	}
};
//#endregion
//#region src/period/period.ts
var Period = class Period {
	start;
	end;
	constructor(start, end) {
		this.start = start;
		this.end = end;
	}
	get hasDuration() {
		return this.start.getTime() !== this.end.getTime();
	}
	static init({ start, end }) {
		if (Period.isEndBeforeStart({
			start,
			end
		})) throw new EndBeforeStart();
		return new Period(start, end);
	}
	static isValid(period) {
		return this.errors(period).length === 0;
	}
	static errors(period) {
		return Period.isEndBeforeStart(period) ? [END_BEFORE_START_ERROR_MESSAGE] : Period.init(period).hasDuration ? [] : [NO_DURATION_ERROR_MESSAGE];
	}
	static isEndBeforeStart({ start, end }) {
		return end.getTime() < start.getTime();
	}
	isOverlapping(otherPeriod) {
		return this.start.getTime() < otherPeriod.end.getTime() && this.end.getTime() > otherPeriod.start.getTime();
	}
	includes(otherPeriod) {
		return this.start.getTime() <= otherPeriod.start.getTime() && this.end.getTime() >= otherPeriod.end.getTime();
	}
	isIncluding(date) {
		const happenAfterStart = date.getTime() >= this.start.getTime();
		const happenBeforeEnd = date.getTime() < this.end.getTime();
		return happenAfterStart && happenBeforeEnd;
	}
	isMergableWith(otherPeriod) {
		return this.start.getTime() <= otherPeriod.end.getTime() && this.end.getTime() >= otherPeriod.start.getTime();
	}
	mergeWith(otherPeriod) {
		const start = new Date(Math.min(otherPeriod.start.getTime(), this.start.getTime()));
		const end = new Date(Math.max(otherPeriod.end.getTime(), this.end.getTime()));
		return new Period(start, end);
	}
	splitOverlapping(otherPeriod) {
		const [firstStart, firstEnd, secondStart, secondEnd] = [
			this.start,
			this.end,
			otherPeriod.start,
			otherPeriod.end
		].sort((a, b) => a.getTime() - b.getTime());
		return [
			Period.init({
				start: firstStart,
				end: firstEnd
			}),
			Period.init({
				start: firstEnd,
				end: secondStart
			}),
			Period.init({
				start: secondStart,
				end: secondEnd
			})
		].filter((period) => period.hasDuration);
	}
	splitInto(parts) {
		if (parts <= 0) return [this];
		const totalDuration = this.end.getTime() - this.start.getTime();
		const partDuration = Math.floor(totalDuration / parts);
		return Array.from({ length: parts }, (_, index) => {
			const start = new Date(this.start.getTime() + index * partDuration);
			const isLastPart = index === parts - 1;
			const calculatedEnd = new Date(start.getTime() + partDuration);
			const end = isLastPart ? this.end : calculatedEnd;
			return Period.init({
				start,
				end
			});
		});
	}
	substract(otherPeriod) {
		return [Period.init({
			start: this.start,
			end: otherPeriod.start
		}), Period.init({
			start: otherPeriod.end,
			end: this.end
		})].filter((period) => period.hasDuration);
	}
	remove(otherPeriod) {
		if (!otherPeriod.isOverlapping(this)) return [this];
		return this.mergeWith(otherPeriod).substract(otherPeriod);
	}
	equals(otherPeriod) {
		const startAtSameTime = this.start.getTime() === otherPeriod.start.getTime();
		const endAtSameTime = this.end.getTime() === otherPeriod.end.getTime();
		return startAtSameTime && endAtSameTime;
	}
	static mergeContiguous(periods) {
		return this.sort(periods).reduce((mergedPeriods, currentPeriod) => {
			const lastMergedPeriod = mergedPeriods.at(mergedPeriods.length - 1);
			if (!lastMergedPeriod) return [currentPeriod];
			if (lastMergedPeriod.isMergableWith(currentPeriod)) {
				const mergedPeriod = lastMergedPeriod.mergeWith(currentPeriod);
				return [...mergedPeriods.slice(0, -1), mergedPeriod];
			}
			return [...mergedPeriods, currentPeriod];
		}, []);
	}
	splitWithInterval(interval) {
		const intervalInMs = interval.inMilliseconds;
		const periodCount = Math.ceil((this.end.getTime() - this.start.getTime()) / intervalInMs);
		return Array.from({ length: periodCount }, (_, index) => {
			const start = new Date(this.start.getTime() + index * intervalInMs);
			const end = new Date(start.getTime() + intervalInMs);
			return Period.init({
				start,
				end: end < this.end ? end : this.end
			});
		});
	}
	isInDay(day) {
		const dayString = OverDate.from(day).dateString;
		const dayStart = OverDate.init({
			date: dayString,
			hour: 0,
			minute: 0
		});
		const dayEnd = OverDate.from(dayStart.date.getTime() + ONE_DAY_IN_MS);
		return this.isOverlapping(Period.init({
			start: dayStart.date,
			end: dayEnd.date
		}));
	}
	toString() {
		return `${this.formatDate(this.start)} - ${this.formatDate(this.end)}`;
	}
	formatDate(date) {
		return new Intl.DateTimeFormat("fr", {
			dateStyle: "long",
			timeStyle: "short"
		}).format(date);
	}
	static sort(periods) {
		return periods.sort((a, b) => a.start.getTime() - b.start.getTime() || a.end.getTime() - b.end.getTime());
	}
	get duration() {
		const startTimestamp = this.start.getTime();
		const endTimestamp = this.end.getTime();
		return Duration.ms(endTimestamp - startTimestamp);
	}
	get id() {
		return `${Duration.ms(this.start.getTime()).inMinutes}-${Duration.ms(this.end.getTime()).inMinutes}`;
	}
};
//#endregion
//#region src/date/date.ts
const PARIS_TIMEZONE = { timeZone: "Europe/Paris" };
const DISPLAY_TIMEZONE = {
	...PARIS_TIMEZONE,
	timeZoneName: "short"
};
const DISPLAY_DATE = {
	...PARIS_TIMEZONE,
	year: "numeric",
	month: "2-digit",
	day: "2-digit"
};
const DISPLAY_TIME = {
	...PARIS_TIMEZONE,
	hour: "2-digit",
	minute: "2-digit"
};
const DISPLAY_DATE_TIME = {
	...DISPLAY_DATE,
	...DISPLAY_TIME
};
var OverDate = class OverDate {
	definition;
	constructor(definition) {
		this.definition = definition;
	}
	static init(init) {
		const definition = OverDate.defineFrom(init);
		return new OverDate(definition);
	}
	static defineFrom(init) {
		const { date, hour, minute = 0 } = init;
		const [year, month, day] = date.split("-");
		const monthlyDate = {
			month,
			day
		};
		if (!isMonthlyDate(monthlyDate)) throw new Error();
		const datetime = `${date}T${formatDateNumberValue(hour)}:${formatDateNumberValue(minute)}`;
		const offset = extractOffset(new Date(datetime));
		return {
			year: +year,
			monthlyDate,
			minute,
			hour,
			offset
		};
	}
	static from(international) {
		const internationalDate = new Date(international);
		const [hourWithPad, minuteWithPad] = Intl.DateTimeFormat("fr", DISPLAY_TIME).format(internationalDate).split(":");
		const hour = +hourWithPad;
		const minute = +minuteWithPad;
		const [day, month, year] = Intl.DateTimeFormat("fr", DISPLAY_DATE).format(internationalDate).split("/");
		const monthlyDate = {
			month,
			day
		};
		if (!isMonthlyDate(monthlyDate) || !isHour(hour) || !isMinute(minute)) throw new Error();
		const offset = extractOffset(internationalDate);
		return new OverDate({
			year: +year,
			monthlyDate,
			hour,
			minute,
			offset
		});
	}
	static fromLocal(local) {
		const year = local.getFullYear();
		const monthlyDate = {
			month: formatDateNumberValue(local.getMonth() + 1),
			day: formatDateNumberValue(local.getDate())
		};
		const hour = local.getHours();
		const minute = local.getMinutes();
		if (!isMonthlyDate(monthlyDate) || !isHour(hour) || !isMinute(minute)) throw new Error();
		const offset = extractOffset(local);
		return new OverDate({
			year,
			monthlyDate,
			hour,
			minute,
			offset
		});
	}
	static now() {
		return OverDate.fromLocal(/* @__PURE__ */ new Date());
	}
	get date() {
		const { month, day } = this.definition.monthlyDate;
		const date = `${this.definition.year}-${month}-${day}`;
		const time = `${formatDateNumberValue(this.hour)}:${formatDateNumberValue(this.minute)}`;
		return /* @__PURE__ */ new Date(`${date}T${time}+${this.definition.offset}:00`);
	}
	get dateString() {
		const { month, day } = this.definition.monthlyDate;
		return `${this.definition.year}-${month}-${day}`;
	}
	get year() {
		return this.definition.year;
	}
	get monthlyDate() {
		return this.definition.monthlyDate;
	}
	get hour() {
		return this.definition.hour;
	}
	get minute() {
		return this.definition.minute;
	}
	get timestamp() {
		return this.date.getTime();
	}
	get period() {
		const start = this.date;
		const end = new Date(this.timestamp + ONE_HOUR_IN_MS);
		return Period.init({
			start,
			end
		});
	}
	isIncludedBy(periods) {
		return periods.some((period) => Period.init(period).isIncluding(this.date));
	}
	plus(duration) {
		return OverDate.from(this.timestamp + duration.inMilliseconds);
	}
	minus(duration) {
		return OverDate.from(this.timestamp - duration.inMilliseconds);
	}
};
const DATE_TEMPORAL_PARTS = 3;
function isDateString(dateString) {
	const date = new Date(dateString);
	if (isNaN(date.getTime())) return false;
	return dateString.split("-").length === DATE_TEMPORAL_PARTS;
}
function isMonthlyDate(monthlyDate) {
	return isMonth(monthlyDate.month) && isDayOf(monthlyDate.month, monthlyDate.day);
}
function isMonth(month) {
	return month.length === 2 && +month > 0 && +month <= 12;
}
function isDayOf(month, day) {
	const maxDayOfTheMonth = month === "02" ? 28 : [
		"04",
		"06",
		"09",
		"11"
	].includes(month) ? 30 : 31;
	return day.length === 2 && +day > 0 && +month <= maxDayOfTheMonth;
}
function isHour(hour) {
	return hour >= 0 && hour < 24;
}
function isMinute(minute) {
	return minute >= 0 && minute < 60;
}
function isOffset(offset) {
	return offset === "01" || offset === "02";
}
function extractOffset(date) {
	const offset = (((new Intl.DateTimeFormat("fr", DISPLAY_TIMEZONE).formatToParts(date).find((value) => value.type === "timeZoneName")?.value)?.match(/UTC[+-](\d)/))?.at(1) ?? "").padStart(2, "0");
	if (!isOffset(offset)) throw new Error(offset);
	return offset;
}
//#endregion
//#region src/date/format-date.utils.ts
function formatDateWithMinutes(date) {
	return new Intl.DateTimeFormat("fr", DISPLAY_DATE_TIME).format(new Date(date));
}
function formatDate(date) {
	return new Intl.DateTimeFormat("fr", DISPLAY_DATE).format(new Date(date));
}
function formatMonthWithYear(date) {
	return new Intl.DateTimeFormat("fr", {
		year: "numeric",
		month: "long"
	}).format(new Date(date));
}
function formatLocalDateTime(date) {
	const [day, month, year, hours, minutes] = new Intl.DateTimeFormat("fr", DISPLAY_DATE_TIME).formatToParts(new Date(date)).filter((part) => part.type !== "literal").map((part) => part.value);
	return `${year}-${month}-${day}T${hours}:${minutes}`;
}
function formatLocalDate(date) {
	const [day, month, year] = new Intl.DateTimeFormat("fr", DISPLAY_DATE).format(new Date(date)).split("/");
	return `${year}-${month}-${day}`;
}
function formatDateWithExplicitMonth(date) {
	const displayOptions = {
		...PARIS_TIMEZONE,
		year: "numeric",
		month: "long"
	};
	return new Intl.DateTimeFormat("fr", displayOptions).format(new Date(date));
}
function formatDateWithExplicitMonthAndDay(date) {
	const displayOptions = {
		...PARIS_TIMEZONE,
		year: "numeric",
		month: "long",
		day: "numeric"
	};
	return new Intl.DateTimeFormat("fr", displayOptions).format(new Date(date));
}
function formatDateDayName(date) {
	const displayOptions = {
		...PARIS_TIMEZONE,
		weekday: "short"
	};
	return new Intl.DateTimeFormat("fr", displayOptions).format(new Date(date));
}
function formatDateDayFullName(date) {
	const displayOptions = {
		...PARIS_TIMEZONE,
		weekday: "long"
	};
	return new Intl.DateTimeFormat("fr", displayOptions).format(new Date(date));
}
function formatDateDayNumber(date) {
	const displayOptions = {
		...PARIS_TIMEZONE,
		day: "numeric"
	};
	return new Intl.DateTimeFormat("fr", displayOptions).format(new Date(date));
}
function formatDateToHumanReadable(date) {
	const displayOptions = {
		...PARIS_TIMEZONE,
		dateStyle: "long",
		timeStyle: "short"
	};
	return new Intl.DateTimeFormat("fr", displayOptions).format(new Date(date));
}
function formatDateDayToHumanReadable(date) {
	const displayOptions = {
		...PARIS_TIMEZONE,
		month: "long",
		day: "numeric",
		weekday: "long"
	};
	return new Intl.DateTimeFormat("fr", displayOptions).format(new Date(date));
}
function formatDateWithHoursAndMinutesOnly(date) {
	return new Intl.DateTimeFormat("fr", DISPLAY_TIME).format(new Date(date));
}
function formatDateNumberValue(value) {
	return value.toString().padStart(2, "0");
}
//#endregion
//#region src/duration/duration.ts
var Duration = class Duration {
	milliseconds;
	constructor(milliseconds) {
		this.milliseconds = milliseconds;
	}
	static ms(milliseconds) {
		return new Duration(milliseconds);
	}
	static get ONE_HOUR() {
		return new Duration(ONE_HOUR_IN_MS);
	}
	static get ONE_DAY() {
		return new Duration(ONE_DAY_IN_MS);
	}
	static get ONE_WEEK() {
		return new Duration(ONE_WEEK_IN_MS);
	}
	static hours(hours) {
		return new Duration(hours * ONE_HOUR_IN_MS);
	}
	get inSeconds() {
		return Math.ceil(this.milliseconds / ONE_SECOND_IN_MS);
	}
	get inMinutes() {
		return Math.ceil(this.milliseconds / ONE_MINUTE_IN_MS);
	}
	get inHours() {
		return Math.ceil(this.milliseconds / ONE_HOUR_IN_MS);
	}
	get inMilliseconds() {
		return this.milliseconds;
	}
	times(multiplier) {
		return new Duration(this.milliseconds * multiplier);
	}
	canBeDividedBy(divider) {
		return this.milliseconds % divider.milliseconds === 0;
	}
	toString() {
		const hours = Math.floor(this.milliseconds / ONE_HOUR_IN_MS);
		const minutes = Math.floor(this.milliseconds % ONE_HOUR_IN_MS / ONE_MINUTE_IN_MS);
		return `${hours.toString()}h${formatDateNumberValue(minutes)}`;
	}
};
//#endregion
//#region src/date/date.utils.ts
function getHourDiff(start, end) {
	return (end.getTime() - start.getTime()) / ONE_HOUR_IN_MS;
}
function roundMinutes(date, round) {
	if (!date) return null;
	const minutes = date.getMinutes();
	if (minutes % round === 0) return date;
	const minutesRounded = Math.round(minutes / round) * round;
	date.setMinutes(minutesRounded);
	return date;
}
function computeTomorrowDate(date) {
	return new Date(date.getTime() + ONE_DAY_IN_MS);
}
//#endregion
//#region src/edition/edition.ts
const BASE_EDITION_STARTS = /* @__PURE__ */ new Date("2023-09-01");
const BASE_EDITION = 49;
var Edition = class Edition {
	static get current() {
		return this.findEdition(/* @__PURE__ */ new Date());
	}
	static findEdition(date) {
		const durationInYears = (date.getTime() - BASE_EDITION_STARTS.getTime()) / ONE_YEAR_IN_MS;
		return BASE_EDITION + Math.floor(durationInYears);
	}
	static on(date) {
		return Edition.findEdition(date);
	}
};
//#endregion
export { BASE_EDITION_STARTS, DAYS_IN_WEEK, Duration, Edition, EndBeforeStart, HOURS_IN_DAY, MINUTES_IN_DAY, MINUTES_IN_HOUR, ONE_DAY_IN_MS, ONE_DAY_IN_SECONDS, ONE_HOUR_IN_MS, ONE_MINUTE_IN_MS, ONE_SECOND_IN_MS, ONE_WEEK_IN_MS, ONE_WEEK_IN_SECONDS, ONE_YEAR_IN_MS, OverDate, Period, PeriodError, QUARTER_IN_MS, THIRTY_SECONDS_IN_MS, TWO_HOURS_IN_MS, computeTomorrowDate, formatDate, formatDateDayFullName, formatDateDayName, formatDateDayNumber, formatDateDayToHumanReadable, formatDateNumberValue, formatDateToHumanReadable, formatDateWithExplicitMonth, formatDateWithExplicitMonthAndDay, formatDateWithHoursAndMinutesOnly, formatDateWithMinutes, formatLocalDate, formatLocalDateTime, formatMonthWithYear, getHourDiff, isDateString, isHour, isMinute, roundMinutes };

//# sourceMappingURL=index.mjs.map